import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { _js, ___p } from '@ifixit/utils/localize';
import { Badge } from '@ifixit/ui/components/badge';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BadgeWallData, BannerBadge } from './leaderboard_types';

/**
 * The badge wall: a banner between the header and the filters showing
 * the badge catalog as a hex grid. Logged out it's the shop window
 * (everything lit, join CTA); logged in it's a progress wall (owned
 * badges lit, missing ones dimmed until the cursor passes over them
 * like a torch). The grid is decorative: the copy and CTA carry the
 * whole message on their own.
 */

/** Grid geometry from the spec: fixed rows, variable columns measured
 *  from the real width. The cell must fit the 34px hex the server
 *  renders. */
const CELL = 34;
const GAP = 9;
/** Cursor effect radii: the lens on lit badges, the torch on dim ones. */
const LENS_RADIUS = 90;
const TORCH_RADIUS = 118;
const TOOLTIP_RADIUS = 34;

export function BadgeWallBanner({
   banner,
   isLoggedIn,
   badgeIndexUrl,
}: {
   banner: BadgeWallData | null;
   isLoggedIn: boolean;
   badgeIndexUrl: string | null;
}) {
   if (!banner || banner.badges.length === 0) {
      return null;
   }
   return (
      <section
         aria-label={_js('Badges')}
         className="mt-6 overflow-hidden rounded-2xl border border-gray-300 bg-white max-md:-mx-4 max-md:rounded-none max-md:border-x-0"
      >
         <div className="flex flex-col gap-6 p-4 md:min-h-[200px] md:flex-row md:items-center md:p-6">
            <BannerCopy banner={banner} isLoggedIn={isLoggedIn} badgeIndexUrl={badgeIndexUrl} />
            <BadgeWall badges={banner.badges} />
         </div>
      </section>
   );
}

function BannerCopy({
   banner,
   isLoggedIn,
   badgeIndexUrl,
}: {
   banner: BadgeWallData;
   isLoggedIn: boolean;
   badgeIndexUrl: string | null;
}) {
   const owned = banner.ownedCount ?? 0;
   const remaining = banner.totalCount - owned;
   return (
      <div className="min-w-0 md:w-[300px] md:shrink-0">
         <Badge className="badge-md badge-gray tabular-nums">
            {isLoggedIn
               ? /* Translators: %1 -> badges earned; %2 -> total badges; */
                 _js('%1 / %2 unlocked', formatNumber(owned), formatNumber(banner.totalCount))
               : /* Translators: %1 -> total badge count; */
                 _js('%1 badges to collect', formatNumber(banner.totalCount))}
         </Badge>
         <h2 className="mt-2 text-xl font-semibold text-gray-950">
            {isLoggedIn
               ? loggedInHeadline(owned, remaining)
               : _js('All up for grabs. None of them yours.')}
         </h2>
         <p className="mt-1 text-sm text-gray-700">
            {isLoggedIn
               ? _js('The wall lights up as you earn them.')
               : _js('An account would be a start.')}
         </p>
         <div className="mt-4 flex flex-wrap items-center gap-4">
            {isLoggedIn ? (
               badgeIndexUrl && (
                  <a
                     href={badgeIndexUrl}
                     className="button-primary button-md no-underline hover:no-underline"
                     onClick={trackBadgeIndexClick}
                  >
                     {_js('View Your Collection')}
                  </a>
               )
            ) : (
               <>
                  <a
                     href="/Join"
                     className="button-primary button-md no-underline hover:no-underline"
                  >
                     {_js('Create an account')}
                  </a>
                  {badgeIndexUrl && (
                     <a
                        href={badgeIndexUrl}
                        className="whitespace-nowrap text-sm font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
                        onClick={trackBadgeIndexClick}
                     >
                        {_js('How badges work')}
                     </a>
                  )}
               </>
            )}
         </div>
      </div>
   );
}

/** v1 put this event on a link covering the whole badge banner, so it fired
 *  for signed-out visitors too. Both CTAs that reach the badge index carry
 *  it, or the series loses that half of its traffic. */
function trackBadgeIndexClick() {
   trackAnalyticsCustomEvent({
      eventCategory: 'Leaderboard',
      eventAction: 'Leaderboard - Badges - View Your Collection',
   });
}

function loggedInHeadline(owned: number, remaining: number): string {
   if (owned === 0) {
      return _js('Your wall is dark. For now.');
   }
   if (remaining === 0) {
      return _js('You have them all. Show-off.');
   }
   /* Translators: %1 -> badges not yet earned; */
   return ___p(
      remaining,
      '%1 badge left to hunt down.',
      '%1 badges left to hunt down.',
      formatNumber(remaining)
   );
}

/**
 * The hex grid. Fixed rows (3 on desktop, 2 on tablet, 1 on phones),
 * variable columns measured with a ResizeObserver; the list is cut to
 * cols * rows, so no badge is ever clipped. The cursor effects write
 * styles straight onto the nodes on a requestAnimationFrame — no React
 * re-render is involved in a mousemove.
 */
function BadgeWall({ badges }: { badges: BannerBadge[] }) {
   const wallRef = useRef<HTMLDivElement | null>(null);
   const cellsRef = useRef<Array<HTMLDivElement | null>>([]);
   const tooltipRef = useRef<HTMLDivElement | null>(null);
   const tooltipNameRef = useRef<HTMLParagraphElement | null>(null);
   const tooltipDescriptionRef = useRef<HTMLParagraphElement | null>(null);
   const [layout, setLayout] = useState<{ cols: number; rows: number } | null>(null);

   useEffect(() => {
      const wall = wallRef.current;
      if (!wall) {
         return;
      }
      const measure = () => {
         const cols = Math.max(0, Math.floor((wall.clientWidth + GAP) / (CELL + GAP)));
         const rows = window.innerWidth >= 768 ? 3 : window.innerWidth >= 640 ? 2 : 1;
         setLayout(previous =>
            previous && previous.cols === cols && previous.rows === rows ? previous : { cols, rows }
         );
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(wall);
      // The row count follows the viewport, which a wall-only observer
      // misses when the width change happens above md.
      window.addEventListener('resize', measure);
      return () => {
         observer.disconnect();
         window.removeEventListener('resize', measure);
      };
   }, []);

   // Memoized because it is the pointer effect's dependency: a fresh array
   // every render would re-register the listeners on every parent render,
   // and the cleanup clears the cells, dimming a lit wall mid-hover.
   const visible = useMemo(
      () => (layout ? badges.slice(0, layout.cols * layout.rows) : []),
      [badges, layout]
   );

   useEffect(() => {
      const wall = wallRef.current;
      if (!wall || visible.length === 0) {
         return;
      }
      if (
         !window.matchMedia('(hover: hover)').matches ||
         window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
         return;
      }

      let frame: number | null = null;
      const pointer = { x: 0, y: 0 };
      // Writing the bubble's text dirties layout and reading its size
      // flushes it, so both are kept to the frames where the hovered badge
      // actually changes; every other frame only writes compositor styles.
      let tipIndex: number | null = null;
      let tipWidth = 0;
      let tipHeight = 0;

      const apply = () => {
         frame = null;
         const rect = wall.getBoundingClientRect();
         const x = pointer.x - rect.left;
         const y = pointer.y - rect.top;
         const cols = Math.max(1, Math.floor((rect.width + GAP) / (CELL + GAP)));
         // justify-end pushes the columns right by whatever width is left
         // over, so the centers carry that offset too or the torch lands
         // one badge to the side.
         const offsetX = Math.max(0, rect.width - (cols * CELL + (cols - 1) * GAP));
         let nearest: { index: number; d: number } | null = null;

         for (const [index, badge] of visible.entries()) {
            const cell = cellsRef.current[index];
            if (!cell) {
               continue;
            }
            // Cell centers come from grid arithmetic, not layout reads:
            // one getBoundingClientRect per frame, not one per badge.
            const cx = offsetX + (index % cols) * (CELL + GAP) + CELL / 2;
            const cy = Math.floor(index / cols) * (CELL + GAP) + CELL / 2;
            const d = Math.hypot(x - cx, y - cy);
            if (nearest === null || d < nearest.d) {
               nearest = { index, d };
            }

            if (badge.owned) {
               const k = Math.max(0, 1 - d / LENS_RADIUS);
               cell.style.transform = k > 0 ? `scale(${1 + k * 0.4})` : '';
               cell.style.zIndex = k > 0 ? '2' : '';
            } else {
               // Inline overrides on top of the dim classes; cleared (not
               // zeroed) at k = 0 so the badge falls back to dim.
               const k = Math.max(0, 1 - d / TORCH_RADIUS);
               cell.style.filter =
                  k > 0 ? `grayscale(${1 - k}) brightness(${0.45 + k * 0.55})` : '';
               cell.style.opacity = k > 0 ? String(0.55 + k * 0.45) : '';
               cell.style.transform = k > 0 ? `scale(${1 + k * 0.28})` : '';
               cell.style.zIndex = k > 0 ? '2' : '';
            }
         }

         const tooltip = tooltipRef.current;
         if (tooltip) {
            if (nearest !== null && nearest.d < TOOLTIP_RADIUS) {
               const cx = offsetX + (nearest.index % cols) * (CELL + GAP) + CELL / 2;
               const rowTop = Math.floor(nearest.index / cols) * (CELL + GAP);
               if (nearest.index !== tipIndex) {
                  tipIndex = nearest.index;
                  if (tooltipNameRef.current) {
                     tooltipNameRef.current.textContent = visible[nearest.index].name;
                  }
                  if (tooltipDescriptionRef.current) {
                     tooltipDescriptionRef.current.textContent = visible[nearest.index].description;
                  }
                  // Measured after the text is set, and only then: the size
                  // holds until the next badge takes the bubble.
                  tipWidth = tooltip.offsetWidth;
                  tipHeight = tooltip.offsetHeight;
               }
               // Keep the bubble inside the wall: the banner card clips
               // overflow, so an unclamped bubble loses its top or its
               // edges (QA round three). Above the badge when there is
               // room, below it on the first rows.
               const tipX = Math.min(Math.max(cx - tipWidth / 2, 0), rect.width - tipWidth);
               let tipY = rowTop - 6 - tipHeight;
               if (tipY < 0) {
                  tipY = rowTop + CELL + 6;
               }
               tooltip.style.transform = `translate(${tipX}px, ${tipY}px)`;
               tooltip.style.opacity = '1';
            } else {
               tooltip.style.opacity = '0';
            }
         }
      };

      const onMouseMove = (event: MouseEvent) => {
         pointer.x = event.clientX;
         pointer.y = event.clientY;
         if (frame === null) {
            frame = requestAnimationFrame(apply);
         }
      };
      const onMouseLeave = () => {
         if (frame !== null) {
            cancelAnimationFrame(frame);
            frame = null;
         }
         cellsRef.current.forEach(cell => {
            if (cell) {
               cell.style.transform = '';
               cell.style.filter = '';
               cell.style.opacity = '';
               cell.style.zIndex = '';
            }
         });
         if (tooltipRef.current) {
            tooltipRef.current.style.opacity = '0';
         }
      };

      wall.addEventListener('mousemove', onMouseMove);
      wall.addEventListener('mouseleave', onMouseLeave);
      return () => {
         wall.removeEventListener('mousemove', onMouseMove);
         wall.removeEventListener('mouseleave', onMouseLeave);
         onMouseLeave();
      };
   }, [visible]);

   return (
      <div className="relative min-w-0 flex-1">
         {/* The names only surface through the pointer tooltip, so the
             grid is decorative to assistive tech; the copy and CTA say
             everything on their own. */}
         <div
            ref={wallRef}
            aria-hidden="true"
            className="grid justify-end"
            style={{
               gridTemplateColumns: layout ? `repeat(${layout.cols}, ${CELL}px)` : undefined,
               gap: `${GAP}px`,
            }}
         >
            {visible.map((badge, index) => (
               <div
                  key={badge.name + index}
                  ref={element => {
                     cellsRef.current[index] = element;
                  }}
                  className={cn(
                     'size-[34px] transition-[transform,filter,opacity] duration-[240ms] ease-[cubic-bezier(.34,1.4,.64,1)] motion-reduce:transition-none [&_svg]:block [&_svg]:size-full',
                     // The dim state lives in classes, not inline styles:
                     // the torch only writes inline overrides, so clearing
                     // them always falls back to dim, never to lit.
                     !badge.owned && 'opacity-55 brightness-[0.45] grayscale'
                  )}
                  dangerouslySetInnerHTML={{ __html: badge.iconSvg }}
               />
            ))}
            <div
               ref={tooltipRef}
               aria-hidden="true"
               className="pointer-events-none absolute left-0 top-0 z-10 max-w-56 rounded-md bg-gray-950 px-2 py-1 text-xs text-white opacity-0 transition-opacity duration-[140ms] motion-reduce:transition-none"
            >
               <p ref={tooltipNameRef} className="my-0 whitespace-nowrap font-medium" />
               <p ref={tooltipDescriptionRef} className="my-0 mt-0.5 text-gray-300" />
            </div>
         </div>
      </div>
   );
}

// Group digits in the page's language, not the browser locale.
function formatNumber(value: number): string {
   try {
      return value.toLocaleString(document.documentElement.lang || undefined);
   } catch {
      return String(value);
   }
}
